<script setup lang="ts">
// #743: 隠しリネージュビューア「この魔導書の系譜」。
// ungated だが backend は ProjectAccessService 経由でアクセス可能な project の
// 系譜しか返さないため、見つけたユーザーが誰でも開けてよい (アクセス制御は backend 側)。
// v0 は素の Vuetify のラフ実装。見た目の作り込みは #605/#606 で行う。
import { useProjectApi } from '~/composables/api/useProjectApi'
import type { LineageViewResponse, LineageFileEvent } from '~/composables/api/useProjectApi'
import type { CreditPerson } from '~/composables/api/useWorkApi'

interface Props {
  projectId: string
}

const props = defineProps<Props>()
const dialog = defineModel<boolean>()

const { getProjectLineageView } = useProjectApi()

const loading = ref(false)
const error = ref<string | null>(null)
const data = ref<LineageViewResponse | null>(null)

const load = async () => {
  if (!props.projectId) {
    data.value = null
    error.value = '魔導書が見つかりませんでした。'
    return
  }
  loading.value = true
  error.value = null
  data.value = null
  try {
    data.value = await getProjectLineageView(props.projectId)
  }
  catch (e) {
    console.error('[LineageViewDialog] getProjectLineageView failed:', e)
    error.value = '系譜の取得に失敗しました。'
  }
  finally {
    loading.value = false
  }
}

watch(dialog, (open) => {
  if (open) load()
})

watch(() => props.projectId, () => {
  if (dialog.value) load()
})

// --- display helpers（トーン: launch bar。C&M 世界観の語彙で統一） ---

const fallbackTitle = (title: string | null | undefined): string =>
  title && title.trim() ? title : '(いにしえの魔導書)'

const fallbackAuthor = (name: string | null | undefined): string =>
  name && name.trim() ? name : '(詠み人知らず)'

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' })
}

// 受け継いだ頁名の結合。多い場合は「ほか N 頁」に畳む。
const INHERITED_NAMES_MAX = 3
const inheritedFilesText = (names: string[]): string => {
  if (!names.length) return ''
  if (names.length <= INHERITED_NAMES_MAX) return `${names.join('、')}を受け継ぎ`
  const shown = names.slice(0, INHERITED_NAMES_MAX).join('、')
  return `${shown}ほか${names.length - INHERITED_NAMES_MAX}頁を受け継ぎ`
}

// #750: 作り手（authored/edited）の編集者を一意キーで v-for する。
const personKey = (person: CreditPerson): string => person.slug ?? person.name

// #836: 「自由に使ってよい素材ですが〜」の注記は撤去した。ライセンスは本文では
// なくバッジ（`LicenseBadge`）で示すので、同じことを文章で繰り返す必要が無い。

// backend は restored_from_deleted / authored / edited を既に除外するが、念のため既知種別以外は描画しない。
const KNOWN_EVENT_TYPES = new Set<LineageFileEvent['type']>(['created', 'copied', 'crystallized', 'renamed', 'duplicated'])

const visibleEvents = (events: LineageFileEvent[]): LineageFileEvent[] =>
  events.filter(e => KNOWN_EVENT_TYPES.has(e.type))

const eventLabel = (event: LineageFileEvent): string => {
  switch (event.type) {
    case 'created':
      return '白紙から書き起こされた頁'
    case 'copied': {
      const author = fallbackAuthor(event.fromAuthorName)
      const title = fallbackTitle(event.fromProjectTitle)
      const suffix = event.fromFileName ? `（元: ${event.fromFileName}）` : ''
      return `${author}さんの『${title}』から書き写した頁${suffix}`
    }
    case 'crystallized': {
      // #750: 自己公開（クリスタル化）。別人の remix でなく本人が結晶化した頁。
      const title = fallbackTitle(event.fromProjectTitle)
      return `『${title}』をクリスタル化した頁`
    }
    case 'renamed':
      return `『${event.oldName ?? '(いにしえの頁)'}』から名を改めた`
    case 'duplicated': {
      const suffix = event.fromFileName ? `（元: ${event.fromFileName}）` : ''
      return `この魔導書の中で写し取った頁${suffix}`
    }
    default:
      return ''
  }
}
</script>

<template>
  <Dialog
    v-model="dialog"
    title="この魔導書の系譜"
    :cancel-button="{ title: 'とじる' }"
    @cancel="dialog = false"
  >
    <div class="lineage-view">
      <div
        v-if="loading"
        class="lineage-view__status"
      >
        読み込み中…
      </div>

      <div
        v-else-if="error"
        class="lineage-view__status lineage-view__status--error"
      >
        {{ error }}
      </div>

      <template v-else-if="data">
        <section class="lineage-section">
          <h3 class="lineage-section__title">
            作り手
          </h3>

          <p
            v-if="!data.credits?.length"
            class="lineage-empty"
          >
            まだ作り手の記録がありません。
          </p>

          <ul
            v-else
            class="lineage-credits"
          >
            <li
              v-for="(line, idx) in data.credits"
              :key="idx"
              class="lineage-credit"
            >
              <span class="lineage-credit__label">{{ line.label }}</span>
              <span class="lineage-credit__body">
                <span class="lineage-credit__creator">
                  <NuxtLink
                    v-if="line.creator.slug"
                    :to="`/c/${line.creator.slug}`"
                    class="lineage-credit__link"
                  >{{ line.creator.name }}</NuxtLink>
                  <template v-else>{{ line.creator.name }}</template>
                  <template v-if="line.editors.length">
                    <span>（原作）</span>
                    <template
                      v-for="editor in line.editors"
                      :key="personKey(editor)"
                    >
                      <span>・</span>
                      <NuxtLink
                        v-if="editor.slug"
                        :to="`/c/${editor.slug}`"
                        class="lineage-credit__link"
                      >{{ editor.name }}</NuxtLink>
                      <template v-else>{{ editor.name }}</template>
                      <span>（改造）</span>
                    </template>
                  </template>
                </span>
                <span class="lineage-credit__meta">
                  <a
                    v-if="line.sourceUrl"
                    :href="line.sourceUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="lineage-credit__source"
                  >出典</a>
                  <LicenseBadge
                    :badge="line.licenseBadge"
                    :tone="line.licenseTone"
                    :url="line.licenseUrl"
                    :note="line.licenseNote"
                  />
                </span>
              </span>
            </li>
          </ul>
        </section>

        <section class="lineage-section">
          <h3 class="lineage-section__title">
            来歴
          </h3>

          <p
            v-if="data.ancestors.length === 0"
            class="lineage-empty"
          >
            この魔導書はまだ誰の系譜も受け継いでいません。ここから物語が始まります。
          </p>

          <v-list
            v-else
            density="compact"
            class="lineage-ancestor-list"
          >
            <v-list-item
              v-for="ancestor in data.ancestors"
              :key="ancestor.projectId"
              class="lineage-ancestor-item"
            >
              <p class="lineage-ancestor-text">
                この魔導書は {{ fallbackAuthor(ancestor.authorName) }} さんの『<NuxtLink
                  v-if="ancestor.parentAccessible"
                  :to="`/playground/${ancestor.projectId}`"
                  class="lineage-ancestor-link"
                >{{ fallbackTitle(ancestor.title) }}</NuxtLink><span v-else>{{ fallbackTitle(ancestor.title) }}</span>』から系譜を受け継いでいます
              </p>
              <p class="lineage-ancestor-meta">
                <span v-if="ancestor.copiedFileNames.length">{{ inheritedFilesText(ancestor.copiedFileNames) }}</span>
                <span v-if="formatDate(ancestor.copiedAt)">
                  <span v-if="ancestor.copiedFileNames.length"> ・ </span>{{ formatDate(ancestor.copiedAt) }}
                </span>
              </p>
            </v-list-item>
          </v-list>
        </section>

        <section class="lineage-section">
          <h3 class="lineage-section__title">
            頁ごとの系譜
          </h3>

          <div
            v-for="file in data.files"
            :key="file.name"
            class="lineage-file"
          >
            <h4 class="lineage-file__name">
              {{ file.name }}
            </h4>
            <ul class="lineage-file__events">
              <li
                v-for="(event, idx) in visibleEvents(file.events)"
                :key="idx"
              >
                {{ eventLabel(event) }}
              </li>
            </ul>
          </div>
        </section>
      </template>
    </div>
  </Dialog>
</template>

<style scoped>
/* Dialog.vue の .fantasy-dialog-text は固定高さでネイティブにはスクロールしないため、
   自前でスクロール可能なラッパを用意する（#743 仕様: 長い場合はスクロール）。 */
.lineage-view {
  height: 100%;
  overflow-y: auto;
  padding-right: 4px;
}

.lineage-view__status {
  padding: 16px 0;
  text-align: center;
}

.lineage-view__status--error {
  color: #b71c1c;
}

.lineage-section {
  margin-bottom: 16px;
}

.lineage-section__title {
  font-size: 100%;
  font-weight: bold;
  margin-bottom: 6px;
  border-bottom: 1px solid rgba(64, 64, 64, 0.3);
}

.lineage-empty {
  opacity: 0.8;
}

.lineage-credits {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.lineage-credit {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 8px;
  font-size: 90%;
}

.lineage-credit__label {
  min-width: 4.5em;
  opacity: 0.75;
}

.lineage-credit__body {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 10px;
  min-width: 0;
}

.lineage-credit__creator {
  font-weight: bold;
  word-break: break-word;
}

.lineage-credit__link {
  color: inherit;
  text-decoration: underline;
}

.lineage-credit__meta {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 90%;
  opacity: 0.7;
  white-space: nowrap;
}

.lineage-credit__source {
  color: inherit;
  text-decoration: underline;
}

.lineage-credit__note {
  margin: 8px 0 0;
  font-size: 78%;
  opacity: 0.7;
  line-height: 1.5;
}

.lineage-ancestor-list {
  background: transparent;
}

.lineage-ancestor-item {
  flex-direction: column;
  align-items: flex-start;
  padding-left: 0;
}

.lineage-ancestor-text {
  margin: 0;
}

.lineage-ancestor-link {
  color: inherit;
  text-decoration: underline;
}

.lineage-ancestor-meta {
  margin: 2px 0 0;
  font-size: 80%;
  opacity: 0.75;
}

.lineage-file {
  margin-bottom: 10px;
}

.lineage-file__name {
  font-size: 90%;
  font-weight: bold;
  word-break: break-all;
}

.lineage-file__events {
  margin: 2px 0 0 1.2em;
  font-size: 85%;
}
</style>
